import { IDateRange } from "../contracts";
import {
  defaultInternalOrdersFilters,
  IPillFilter,
  useStore,
  IInternalPurchaseOrderItemsCommonFilters,
  IPropertyFilter
} from "../store";

export interface IInternalItemsRedirectionFilters {
  orderCreationRange?: IDateRange;
  requestedDeliveryRange?: IDateRange;
  confirmedDeliveryRange?: IDateRange;
  calculatedConfirmRange?: IDateRange;
  status?: number;
  meetRequested?: IPillFilter[];
  onTime?: IPillFilter[];
  property?: IPropertyFilter;
  internalPurchaseOrderNumber?: string[];
  salesOrderNumber?: string[];
  siteName?: string[];
  siteId?: string[];
}

export const useInternalItemsFiltersActions = () => {
  const [_, setState] = useStore();

  const setOrdersCreationRange = (range: IDateRange) => {
    setState(old => ({
      ...old,
      internalItemsCommonFilters: { ...old.internalItemsCommonFilters, orderCreationRange: range }
    }));
  };

  const setInternalOrdersStatus = (status: number) => {
    const newStatus = (old: number[], status: number) => {
      if (old.includes(status)) {
        return old.filter(s => s !== status);
      } else {
        return [...old, status];
      }
    };

    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        statuses: newStatus(old.internalItemsCommonFilters.statuses || [], status)
      }
    }));
  };

  const setOrdersRequestedDeliveryRange = (range: IDateRange) => {
    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        requestedDeliveryRange: range
      }
    }));
  };

  const setCurrentConfirmedDateRange = (range: IDateRange) => {
    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        currentConfirmedDateRange: range
      }
    }));
  };

  const setDeliveryStatus = (status: number) => {
    const newStatus = (old: number[], status: number) => {
      if (old.includes(status)) {
        return old.filter(s => s !== status);
      } else {
        return [...old, status];
      }
    };

    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        statuses: newStatus(old.internalItemsCommonFilters.statuses || [], status)
      }
    }));
  };

  const setDelayedRequestedDate = (filter: IPillFilter[]) => {
    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        delayedRequestedDate: filter
      }
    }));
  };

  const setInternalItemsServerFilter = (filter: IInternalPurchaseOrderItemsCommonFilters) => {
    setState(old => ({
      ...old,
      internalItemsServerFilter: filter
    }));
  };

  const clearOrdersRequestedDeliveryRange = () => {
    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        requestedDeliveryRange: undefined
      }
    }));
  };

  const clearCurrentConfirmedDateRange = () => {
    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        currentConfirmedDateRange: undefined
      }
    }));
  };

  const clearDeliveryStatus = () => {
    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        statuses: undefined
      }
    }));
  };

  const clearDelayedRequestedDate = () => {
    setState(old => ({
      ...old,
      internalItemsCommonFilters: {
        ...old.internalItemsCommonFilters,
        delayedRequestedDate: defaultInternalOrdersFilters.delayedRequestedDate
      }
    }));
  };

  /**
   * Sets pre-filled values in individual column filters in the Purchase Order Items list view.
   */
  const setItemsRedirectionFilters = (filters: IInternalItemsRedirectionFilters) => {
    const {
      orderCreationRange,
      requestedDeliveryRange,
      confirmedDeliveryRange,
      status,
      meetRequested,
      onTime,
      internalPurchaseOrderNumber,
      salesOrderNumber,
      siteName,
      siteId
    } = filters;

    setState(old => {
      const serverFilterSettings = [
        {
          columnKey: "InternalPurchaseOrderNumber",
          filterValues: internalPurchaseOrderNumber ? internalPurchaseOrderNumber : [],
          filterText: "",
          sortIndex: -1,
          sortDirection: "",
          groupIndex: -1
        },
        {
          columnKey: "SalesOrderNumber",
          filterValues: salesOrderNumber ? salesOrderNumber : [],
          filterText: "",
          sortIndex: -1,
          sortDirection: "",
          groupIndex: -1
        },
        {
          columnKey: "SiteName",
          filterValues: siteName ? siteName : [],
          filterText: "",
          sortIndex: -1,
          sortDirection: "",
          groupIndex: -1
        },
        {
          columnKey: "SiteId",
          filterValues: siteId ? siteId : [],
          filterText: "",
          sortIndex: -1,
          sortDirection: "",
          groupIndex: -1
        }
      ].filter(x => x.filterValues.length > 0);

      const serverFilterModel = {
        internalPurchaseOrderNumber: {
          filterType: "set",
          values: internalPurchaseOrderNumber
        },
        salesOrderNumber: {
          filterType: "set",
          values: salesOrderNumber
        },
        siteName: {
          filterType: "set",
          values: siteName
        },
        siteId: {
          filterType: "set",
          values: siteId
        }
      };

      const filteredModel = Object.fromEntries(
        Object.entries(serverFilterModel).filter(
          ([key, value]) => value.values && value.values.length > 0
        )
      );

      return {
        ...old,
        internalItemsCommonFilters: {
          ...old.internalItemsCommonFilters,
          orderCreationRange: orderCreationRange,
          requestedDeliveryRange,
          confirmedDeliveryRange,
          statuses: status === undefined ? undefined : [status],
          meetRequested: meetRequested || defaultInternalOrdersFilters.meetRequested,
          onTime: onTime || defaultInternalOrdersFilters.onTime
        },
        serverSideFilterSettings: serverFilterSettings,
        internalServerSideFilterSettings: serverFilterSettings,
        serverSideFilterModel: filteredModel
      };
    });
  };

  return {
    setOrdersCreationRange,
    setOrdersRequestedDeliveryRange,
    setInternalItemsServerFilter,
    setDelayedRequestedDate,
    setCurrentConfirmedDateRange,
    setDeliveryStatus,
    clearCurrentConfirmedDateRange,
    clearDeliveryStatus,
    clearOrdersRequestedDeliveryRange,
    clearDelayedRequestedDate,
    setItemsRedirectionFilters,
    setInternalOrdersStatus
  };
};
